/**
 * Super dealers — onboarding and day-two management.
 *
 * The create form follows the sectioned "create a partner account" pattern
 * researched on Mobbin (Stripe's grouped create-customer sheet and Shopify's
 * "New company" page): one column, labelled sections, helper text under the
 * field that needs explaining, and the credentials revealed exactly once.
 *
 * Nothing here writes to a wallet directly. Creating a dealer makes the row
 * plus its wallet; the optional first float is a separate funding call that the
 * API records in the ledger with an audit event.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { errorMessage, shortDate, tzs, useAdminQuery, useDebouncedValue } from "../lib/admin";
import { adminApi } from "../lib/api";
import {
  Building2,
  Check,
  Copy,
  KeyRound,
  Pause,
  Play,
  Plus,
  Search,
  ShieldAlert,
  Wallet,
  X,
} from "lucide-react";

interface DealerBalances {
  available_minor: number;
}

interface DealerRow {
  id: string;
  code: string;
  business_name: string;
  owner_name: string | null;
  phone: string;
  email: string | null;
  region: string | null;
  district: string | null;
  state: string;
  wallet_id: string | null;
  balances: DealerBalances | null;
  daily_transfer_limit_minor: number;
  txn_transfer_limit_minor: number;
  created_at: string | null;
}

interface DealerList {
  data: DealerRow[];
  total: number;
}

interface AgentNetworkRow {
  id: string;
  code: string;
  business_name: string;
  phone: string;
  status: string;
  superdealer_id: string | null;
  dealer_code: string | null;
  available_minor: number;
}

interface AgentList {
  data: AgentNetworkRow[];
}

interface CreateResult {
  id: string;
  code: string;
  state: string;
  wallet_id: string | null;
  temp_pin: string;
}

interface PinResult {
  id: string;
  code: string;
  phone: string;
  temp_pin: string;
}

const REGIONS = [
  "Dar es Salaam",
  "Arusha",
  "Mwanza",
  "Dodoma",
  "Mbeya",
  "Tanga",
  "Morogoro",
  "Kilimanjaro",
  "Geita",
  "Kigoma",
  "Kagera",
  "Manyara",
  "Mara",
  "MTwara",
  "Musoma",
  "Shinyanga",
  "Simiyu",
  "Singida",
  "Iringa",
  "Njombe",
  "Katavi",
  "Rukwa",
  "Ruvuma",
  "Pwani",
  "Lindi",
  "Mtwara",
  "Zanzibar Urban",
  "Zanzibar North",
  "Zanzibar South",
  "Pemba",
  "Mafia",
];

const STATES = ["all", "active", "pending", "suspended", "terminated"] as const;

const DEFAULT_TXN_LIMIT_TZS = 100_000;
const DEFAULT_DAILY_LIMIT_TZS = 1_000_000;

function stateTone(state: string) {
  if (state === "active") return "ev-badge ev-badge-mint";
  if (state === "pending" || state === "draft") return "ev-badge ev-badge-amber";
  if (state === "suspended" || state === "terminated") return "ev-badge ev-badge-red";
  return "ev-badge";
}

/** TZS as typed by a human, minor units as the API wants them. */
function toMinor(tzsInput: string): number {
  const value = Number(String(tzsInput).replace(/[^\d.]/g, ""));
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

function fromMinor(minor: number | null | undefined): string {
  if (!minor) return "";
  return String(minor / 100);
}

function randomPin(): string {
  return String(Math.floor(100_000 + Math.random() * 900_000));
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function CopyChip({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="ev-btn ev-btn-sm ev-btn-ghost"
      onClick={() => {
        void copyText(value).then((ok) => {
          if (!ok) return;
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        });
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied" : `Copy ${label}`}
    </button>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="onboard-section">
      <div className="onboard-section-head">
        <h4>{title}</h4>
        {hint && <p className="ev-body-micro muted">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const EMPTY_FORM = {
  business_name: "",
  owner_name: "",
  phone: "",
  email: "",
  region: REGIONS[0],
  district: "",
  temp_pin: "",
  state: "active",
  txn_limit: String(DEFAULT_TXN_LIMIT_TZS),
  daily_limit: String(DEFAULT_DAILY_LIMIT_TZS),
  float_amount: "",
  float_reference: "",
  reason: "",
};

export function SuperDealers() {
  const queryClient = useQueryClient();
  const [stateFilter, setStateFilter] = useState<(typeof STATES)[number]>("all");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<"closed" | "create" | "manage">("closed");
  const [selected, setSelected] = useState<DealerRow | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [created, setCreated] = useState<CreateResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // manage-mode edits, seeded when a dealer is opened
  const [edit, setEdit] = useState({ ...EMPTY_FORM });
  const [manageReason, setManageReason] = useState("");
  const [newPin, setNewPin] = useState<string | null>(null);
  const [attachPick, setAttachPick] = useState("");

  // Every admin write carries the reason into the audit trail as a query param,
  // because the shared client has no per-request header option.
  const reasonQuery = () => ({ x_admin_reason: manageReason.trim() || undefined });

  const q = useDebouncedValue(search);
  const list = useAdminQuery<DealerList>(
    ["superdealers", stateFilter, q],
    "/admin/v1/superdealers",
    { state: stateFilter === "all" ? undefined : stateFilter, limit: 200 },
  );

  const rows = useMemo(() => {
    const all = list.data?.data ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((row) =>
      [row.code, row.business_name, row.owner_name, row.phone, row.region, row.district]
        .filter(Boolean)
        .some((cell) => String(cell).toLowerCase().includes(needle)),
    );
  }, [list.data, q]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
    void queryClient.invalidateQueries({ queryKey: ["superdealers"] });
  };

  const onboard = useMutation({
    mutationFn: async (input: typeof EMPTY_FORM): Promise<CreateResult> => {
      const reason = input.reason.trim() || undefined;
      const result = await adminApi.post<CreateResult>(
        "/admin/v1/superdealers",
        {
          business_name: input.business_name.trim(),
          owner_name: input.owner_name.trim() || undefined,
          phone: input.phone.trim(),
          email: input.email.trim() || undefined,
          region: input.region,
          district: input.district.trim() || undefined,
          temp_pin: input.temp_pin.trim() || undefined,
          state: input.state,
          txn_transfer_limit_minor: toMinor(input.txn_limit) || undefined,
          daily_transfer_limit_minor: toMinor(input.daily_limit) || undefined,
        },
        { query: { x_admin_reason: reason } },
      );
      const floatMinor = toMinor(input.float_amount);
      if (floatMinor > 0) {
        await adminApi.post(`/admin/v1/superdealers/${result.id}/fund`, undefined, {
          query: {
            amount_minor: floatMinor,
            reference: input.float_reference.trim() || undefined,
            x_admin_reason: reason,
          },
        });
      }
      return result;
    },
    onSuccess: (result) => {
      setCreated(result);
      invalidate();
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const patch = useMutation({
    mutationFn: (input: { id: string; changes: Record<string, unknown> }) =>
      adminApi.patch(`/admin/v1/superdealers/${input.id}`, input.changes, { query: reasonQuery() }),
    onSuccess: () => {
      invalidate();
      setFeedback("Saved");
      setActionError(null);
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const setState = useMutation({
    mutationFn: (input: { id: string; state: string }) =>
      adminApi.post(`/admin/v1/superdealers/${input.id}/state`, { state: input.state }, {
        query: reasonQuery(),
      }),
    onSuccess: () => invalidate(),
    onError: (error) => setActionError(errorMessage(error)),
  });

  const resetPin = useMutation({
    mutationFn: (input: { id: string; new_pin?: string }) =>
      adminApi.post<PinResult>(
        `/admin/v1/superdealers/${input.id}/reset-pin`,
        { new_pin: input.new_pin },
        { query: reasonQuery() },
      ),
    onSuccess: (result) => {
      setNewPin(result.temp_pin);
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const fund = useMutation({
    mutationFn: (input: { id: string; amount_minor: number; reference: string }) =>
      adminApi.post(`/admin/v1/superdealers/${input.id}/fund`, undefined, {
        query: {
          amount_minor: input.amount_minor,
          reference: input.reference || undefined,
          ...reasonQuery(),
        },
      }),
    onSuccess: () => {
      invalidate();
      setFeedback("Float credited");
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  // Agents are the dealer's network; only this drawer moves an existing agent in or out.
  const agentsQ = useAdminQuery<AgentList>(
    ["agents", "network"],
    "/admin/v1/agents",
    { page_size: 200 },
    mode === "manage" && !!selected,
  );
  const allAgents = agentsQ.data?.data ?? [];
  const networkAgents = useMemo(
    () => allAgents.filter((agent) => agent.superdealer_id === selected?.id),
    [allAgents, selected],
  );
  const unattached = useMemo(
    () => allAgents.filter((agent) => !agent.superdealer_id),
    [allAgents],
  );

  const assign = useMutation({
    mutationFn: (input: { agentId: string; dealer_id: string | null }) =>
      adminApi.patch(`/admin/v1/agents/${input.agentId}/dealer`, { dealer_id: input.dealer_id }, {
        query: reasonQuery(),
      }),
    onSuccess: () => {
      void agentsQ.refetch();
      setAttachPick("");
      setActionError(null);
      setFeedback("Network updated");
      setTimeout(() => setFeedback(null), 2500);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setCreated(null);
    setActionError(null);
    setMode("create");
  };

  const openManage = (dealer: DealerRow) => {
    setSelected(dealer);
    setEdit({
      ...EMPTY_FORM,
      business_name: dealer.business_name,
      owner_name: dealer.owner_name ?? "",
      phone: dealer.phone,
      email: dealer.email ?? "",
      region: dealer.region ?? REGIONS[0],
      district: dealer.district ?? "",
      txn_limit: fromMinor(dealer.txn_transfer_limit_minor),
      daily_limit: fromMinor(dealer.daily_transfer_limit_minor),
    });
    setActionError(null);
    setFeedback(null);
    setManageReason("");
    setNewPin(null);
    setAttachPick("");
    setMode("manage");
  };

  const close = () => {
    setMode("closed");
    setSelected(null);
    setCreated(null);
  };

  const phoneValid = form.phone.replace(/\D/g, "").length >= 9;
  const nameValid = form.business_name.trim().length >= 2;
  const pinValid = form.temp_pin === "" || /^\d{4,6}$/.test(form.temp_pin.trim());
  const canSubmit = nameValid && phoneValid && pinValid && !onboard.isPending;

  const totalFloat = rows.reduce((sum, row) => sum + (row.balances?.available_minor ?? 0), 0);

  return (
    <>
      <PageHead
        title="Super Dealers"
        description="Onboard the dealers who hold float and recruit agents, then manage their limits, state and first credit."
        actions={
          <>
            <span className="ev-badge">
              <Wallet size={12} /> {tzs(totalFloat)} held
            </span>
            <button className="ev-btn ev-btn-primary" onClick={openCreate}>
              <Plus size={16} /> Onboard super dealer
            </button>
          </>
        }
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-toggle-group">
          {STATES.map((s) => (
            <button
              key={s}
              className={`admin-toggle ${stateFilter === s ? "admin-toggle-active" : ""}`}
              onClick={() => setStateFilter(s)}
            >
              {s === "all" ? "All dealers" : s}
            </button>
          ))}
        </div>
        <div style={{ position: "relative" }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ev-slate-400)",
              pointerEvents: "none",
            }}
          />
          <input
            className="ev-input"
            placeholder="Search code, business, owner or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36, width: 300 }}
          />
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <ShieldAlert size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading dealers…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && rows.length === 0 && (
        <EmptyState>
          {search
            ? "No dealer matches that search."
            : "No super dealers yet. Onboard the first one — the portal is dealer.skttanzania.co.tz."}
        </EmptyState>
      )}

      {rows.length > 0 && (
        <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Code</th>
                  <th className="admin-table-th">Business</th>
                  <th className="admin-table-th">Sign-in phone</th>
                  <th className="admin-table-th">Where</th>
                  <th className="admin-table-th">Float</th>
                  <th className="admin-table-th">Transfer limits</th>
                  <th className="admin-table-th">State</th>
                  <th className="admin-table-th" />
                </tr>
              </thead>
              <tbody>
                {rows.map((dealer) => (
                  <tr key={dealer.id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Code">
                      <span className="ev-body-mono">{dealer.code}</span>
                    </td>
                    <td className="admin-table-td" data-label="Business">
                      <div className="admin-cell-stack">
                        <span className="ev-body-semibold">{dealer.business_name}</span>
                        <span className="ev-body-micro muted">{dealer.owner_name ?? "no owner named"}</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Sign-in phone">
                      <span className="ev-body-mono">{dealer.phone}</span>
                    </td>
                    <td className="admin-table-td" data-label="Where">
                      <div className="admin-cell-stack">
                        <span className="ev-body-micro">{dealer.region ?? "—"}</span>
                        <span className="ev-body-micro muted">{dealer.district ?? ""}</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Float">
                      <span className="ev-body-mono">{tzs(dealer.balances?.available_minor ?? 0)}</span>
                    </td>
                    <td className="admin-table-td" data-label="Transfer limits">
                      <div className="admin-cell-stack">
                        <span className="ev-body-micro">{tzs(dealer.txn_transfer_limit_minor)} per transfer</span>
                        <span className="ev-body-micro muted">{tzs(dealer.daily_transfer_limit_minor)} per day</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="State">
                      <span className={stateTone(dealer.state)}>{dealer.state}</span>
                    </td>
                    <td className="admin-table-td" data-label="">
                      <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => openManage(dealer)}>
                        <Building2 size={16} /> Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mode === "create" && (
        <div className="admin-drawer-overlay" onClick={close}>
          <div className="admin-drawer onboard-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="onboard-drawer-head">
              <div>
                <h3>Onboard a super dealer</h3>
                <p className="ev-body-micro muted">
                  Six sections. Only the business name and the sign-in phone are mandatory.
                </p>
              </div>
              <button className="ev-icon-btn" onClick={close} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            {created ? (
              <div className="onboard-credentials">
                <h4>
                  <Check size={16} /> {created.code} is ready
                </h4>
                <p className="ev-body-micro muted">
                  Share these once, over a channel you trust. The PIN is not stored in plain text, so it
                  cannot be shown again after this panel closes.
                </p>
                <dl className="onboard-credential-list">
                  <div>
                    <dt>Dealer code</dt>
                    <dd>
                      <span className="ev-body-mono">{created.code}</span>
                      <CopyChip value={created.code} label="code" />
                    </dd>
                  </div>
                  <div>
                    <dt>Sign-in phone</dt>
                    <dd>
                      <span className="ev-body-mono">{form.phone}</span>
                      <CopyChip value={form.phone} label="phone" />
                    </dd>
                  </div>
                  <div>
                    <dt>Temporary PIN</dt>
                    <dd>
                      <span className="ev-body-mono">
                        <KeyRound size={13} /> {created.temp_pin}
                      </span>
                      <CopyChip value={created.temp_pin} label="PIN" />
                    </dd>
                  </div>
                  <div>
                    <dt>Portal</dt>
                    <dd>
                      <span className="ev-body-mono">dealer.skttanzania.co.tz</span>
                    </dd>
                  </div>
                  <div>
                    <dt>State</dt>
                    <dd>
                      <span className={stateTone(created.state)}>{created.state}</span>
                    </dd>
                  </div>
                </dl>
                <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                  <button
                    className="ev-btn ev-btn-outline"
                    onClick={() => {
                      setCreated(null);
                      setForm({ ...EMPTY_FORM });
                    }}
                  >
                    <Plus size={16} /> Onboard another
                  </button>
                  <button className="ev-btn ev-btn-primary" onClick={close}>
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canSubmit) onboard.mutate(form);
                }}
              >
                <Section title="Business identity" hint="As it appears on the dealer's licence.">
                  <div className="admin-field">
                    <label htmlFor="sd-name">Business name *</label>
                    <input
                      id="sd-name"
                      className="ev-input"
                      value={form.business_name}
                      onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                      placeholder="Highland Float Ltd"
                      required
                    />
                  </div>
                  <div className="onboard-grid-2">
                    <div className="admin-field">
                      <label htmlFor="sd-owner">Owner name</label>
                      <input
                        id="sd-owner"
                        className="ev-input"
                        value={form.owner_name}
                        onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                        placeholder="Asha Juma"
                      />
                    </div>
                    <div className="admin-field">
                      <label htmlFor="sd-email">E-mail</label>
                      <input
                        id="sd-email"
                        className="ev-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="asha@highland.example"
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Sign-in number" hint="MSISDN the dealer uses at dealer.skttanzania.co.tz.">
                  <div className="admin-field">
                    <label htmlFor="sd-phone">Phone *</label>
                    <input
                      id="sd-phone"
                      className="ev-input"
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+ ]/g, "") })}
                      placeholder="255754001122"
                      required
                    />
                    {form.phone && !phoneValid && (
                      <span className="ev-body-micro muted">Needs at least 9 digits.</span>
                    )}
                  </div>
                </Section>

                <Section title="Where they operate">
                  <div className="onboard-grid-2">
                    <div className="admin-field">
                      <label htmlFor="sd-region">Region</label>
                      <select
                        id="sd-region"
                        className="ev-input"
                        value={form.region}
                        onChange={(e) => setForm({ ...form, region: e.target.value })}
                      >
                        {REGIONS.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-field">
                      <label htmlFor="sd-district">District / ward</label>
                      <input
                        id="sd-district"
                        className="ev-input"
                        value={form.district}
                        onChange={(e) => setForm({ ...form, district: e.target.value })}
                        placeholder="Mbagala"
                      />
                    </div>
                  </div>
                </Section>

                <Section
                  title="Portal access"
                  hint="Leave the PIN empty and the platform generates one for you."
                >
                  <div className="admin-field">
                    <label htmlFor="sd-pin">Temporary PIN (4–6 digits)</label>
                    <div className="onboard-pin-row">
                      <input
                        id="sd-pin"
                        className="ev-input"
                        inputMode="numeric"
                        maxLength={6}
                        value={form.temp_pin}
                        onChange={(e) =>
                          setForm({ ...form, temp_pin: e.target.value.replace(/\D/g, "").slice(0, 6) })
                        }
                        placeholder="auto-generate"
                      />
                      <button
                        type="button"
                        className="ev-btn ev-btn-outline ev-btn-sm"
                        onClick={() => setForm({ ...form, temp_pin: randomPin() })}
                      >
                        <KeyRound size={16} /> Generate
                      </button>
                    </div>
                    {!pinValid && <span className="ev-body-micro muted">Digits only, 4 to 6 of them.</span>}
                  </div>
                </Section>

                <Section
                  title="Limits and state"
                  hint="A pending dealer exists in the system but cannot sign in until it is activated."
                >
                  <div className="onboard-grid-2">
                    <div className="admin-field">
                      <label htmlFor="sd-txn">Per transfer (TZS)</label>
                      <input
                        id="sd-txn"
                        className="ev-input"
                        inputMode="numeric"
                        value={form.txn_limit}
                        onChange={(e) => setForm({ ...form, txn_limit: e.target.value.replace(/[^\d.]/g, "") })}
                      />
                    </div>
                    <div className="admin-field">
                      <label htmlFor="sd-daily">Per day (TZS)</label>
                      <input
                        id="sd-daily"
                        className="ev-input"
                        inputMode="numeric"
                        value={form.daily_limit}
                        onChange={(e) =>
                          setForm({ ...form, daily_limit: e.target.value.replace(/[^\d.]/g, "") })
                        }
                      />
                    </div>
                  </div>
                  {toMinor(form.txn_limit) > toMinor(form.daily_limit) && (
                    <p className="admin-note">
                      A single transfer is larger than the daily cap. The dealer will never reach the daily
                      limit.
                    </p>
                  )}
                  <div className="admin-field">
                    <label htmlFor="sd-state">Starting state</label>
                    <div className="admin-toggle-group">
                      {["active", "pending"].map((s) => (
                        <button
                          key={s}
                          type="button"
                          className={`admin-toggle ${form.state === s ? "admin-toggle-active" : ""}`}
                          onClick={() => setForm({ ...form, state: s })}
                        >
                          {s === "active" ? "Active now" : "Pending activation"}
                        </button>
                      ))}
                    </div>
                  </div>
                </Section>

                <Section
                  title="First float"
                  hint="Optional. Otherwise the dealer reports a bank transfer and you approve the slip."
                >
                  <div className="onboard-grid-2">
                    <div className="admin-field">
                      <label htmlFor="sd-float">Amount (TZS)</label>
                      <input
                        id="sd-float"
                        className="ev-input"
                        inputMode="numeric"
                        value={form.float_amount}
                        onChange={(e) =>
                          setForm({ ...form, float_amount: e.target.value.replace(/[^\d.]/g, "") })
                        }
                        placeholder="0"
                      />
                    </div>
                    <div className="admin-field">
                      <label htmlFor="sd-float-ref">Bank / receipt reference</label>
                      <input
                        id="sd-float-ref"
                        className="ev-input"
                        value={form.float_reference}
                        onChange={(e) => setForm({ ...form, float_reference: e.target.value })}
                        placeholder="CRDB-0411-88213"
                      />
                    </div>
                  </div>
                </Section>

                <Section title="Why" hint="Written to the audit trail next to this action.">
                  <div className="admin-field">
                    <label htmlFor="sd-reason">Onboarding note</label>
                    <textarea
                      id="sd-reason"
                      className="ev-textarea"
                      rows={2}
                      value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="Signed 04/11, TIN verified, Mwanza cluster"
                      style={{ resize: "vertical" }}
                    />
                  </div>
                </Section>

                <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
                  <button className="ev-btn ev-btn-primary" type="submit" disabled={!canSubmit}>
                    <Plus size={16} /> Create super dealer
                  </button>
                  <button className="ev-btn ev-btn-ghost" type="button" onClick={close}>
                    Cancel
                  </button>
                  {!nameValid && (
                    <span className="ev-body-micro muted" style={{ alignSelf: "center" }}>
                      Business name needs at least 2 characters.
                    </span>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {mode === "manage" && selected && (
        <div className="admin-drawer-overlay" onClick={close}>
          <div className="admin-drawer onboard-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="onboard-drawer-head">
              <div>
                <h3>{selected.business_name}</h3>
                <p className="ev-body-micro muted">
                  {selected.code} · onboarded {shortDate(selected.created_at)}
                </p>
              </div>
              <button className="ev-icon-btn" onClick={close} aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="admin-detail-grid">
              <div>
                <div className="ev-body-micro muted">Float available</div>
                <div className="ev-body-mono">{tzs(selected.balances?.available_minor ?? 0)}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">State</div>
                <div>
                  <span className={stateTone(selected.state)}>{selected.state}</span>
                </div>
              </div>
              <div>
                <div className="ev-body-micro muted">Sign-in phone</div>
                <div className="ev-body-mono">{selected.phone}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Owner</div>
                <div className="ev-body">{selected.owner_name ?? "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">E-mail</div>
                <div className="ev-body">{selected.email ?? "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Where</div>
                <div className="ev-body">
                  {selected.region ?? "—"} {selected.district ? `· ${selected.district}` : ""}
                </div>
              </div>
            </div>

            <div className="admin-field" style={{ marginTop: 12 }}>
              <label htmlFor="sd-manage-reason">Reason for changes</label>
              <input
                id="sd-manage-reason"
                className="ev-input"
                value={manageReason}
                onChange={(e) => setManageReason(e.target.value)}
                placeholder="e.g. dealer moved shop, boss call 04/11"
              />
              <p className="ev-body-micro muted">
                Recorded against every save, state change, credit and PIN reset made below.
              </p>
            </div>

            {feedback && (
              <p className="ev-body-micro muted" style={{ marginTop: 12 }}>
                <Check size={13} /> {feedback}
              </p>
            )}

            <Section title="Details and limits" hint="Changes are audited with your reason.">
              <div className="onboard-grid-2">
                <div className="admin-field">
                  <label htmlFor="sd-m-name">Business name</label>
                  <input
                    id="sd-m-name"
                    className="ev-input"
                    value={edit.business_name}
                    onChange={(e) => setEdit({ ...edit, business_name: e.target.value })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-m-owner">Owner name</label>
                  <input
                    id="sd-m-owner"
                    className="ev-input"
                    value={edit.owner_name}
                    onChange={(e) => setEdit({ ...edit, owner_name: e.target.value })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-m-email">E-mail</label>
                  <input
                    id="sd-m-email"
                    className="ev-input"
                    value={edit.email}
                    onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-m-district">District / ward</label>
                  <input
                    id="sd-m-district"
                    className="ev-input"
                    value={edit.district}
                    onChange={(e) => setEdit({ ...edit, district: e.target.value })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-m-txn">Per transfer (TZS)</label>
                  <input
                    id="sd-m-txn"
                    className="ev-input"
                    inputMode="numeric"
                    value={edit.txn_limit}
                    onChange={(e) => setEdit({ ...edit, txn_limit: e.target.value.replace(/[^\d.]/g, "") })}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-m-daily">Per day (TZS)</label>
                  <input
                    id="sd-m-daily"
                    className="ev-input"
                    inputMode="numeric"
                    value={edit.daily_limit}
                    onChange={(e) => setEdit({ ...edit, daily_limit: e.target.value.replace(/[^\d.]/g, "") })}
                  />
                </div>
              </div>
              <button
                className="ev-btn ev-btn-primary"
                type="button"
                disabled={patch.isPending}
                onClick={() =>
                  patch.mutate({
                    id: selected.id,
                    changes: {
                      business_name: edit.business_name.trim(),
                      owner_name: edit.owner_name.trim() || undefined,
                      email: edit.email.trim() || undefined,
                      district: edit.district.trim() || undefined,
                      txn_transfer_limit_minor: toMinor(edit.txn_limit) || undefined,
                      daily_transfer_limit_minor: toMinor(edit.daily_limit) || undefined,
                    },
                  })
                }
              >
                <Check size={16} /> Save changes
              </button>
            </Section>

            <Section title="Access" hint="Suspending blocks sign-in immediately; existing sessions die on refresh.">
              <div className="onboard-actions">
                {selected.state !== "active" && (
                  <button
                    className="ev-btn ev-btn-outline ev-btn-sm"
                    onClick={() => setState.mutate({ id: selected.id, state: "active" })}
                  >
                    <Play size={15} /> Activate
                  </button>
                )}
                {selected.state === "active" && (
                  <button
                    className="ev-btn ev-btn-outline ev-btn-sm"
                    onClick={() => setState.mutate({ id: selected.id, state: "suspended" })}
                  >
                    <Pause size={15} /> Suspend
                  </button>
                )}
                {selected.state !== "terminated" && (
                  <button
                    className="ev-btn ev-btn-ghost ev-btn-sm"
                    onClick={() => setState.mutate({ id: selected.id, state: "terminated" })}
                  >
                    <X size={15} /> Terminate
                  </button>
                )}
              </div>

              <p className="ev-body-micro muted">
                Sign-in PIN: the hash is not readable, so a dealer who loses it cannot be reminded -
                reset it. A device already signed in keeps its session until the access token expires
                (15 minutes) and then needs the new PIN; float transfers always need the new PIN.
              </p>
              {newPin ? (
                <dl className="onboard-credential-list">
                  <div>
                    <dt>New PIN for {selected.business_name}</dt>
                    <dd>
                      <span className="ev-body-mono" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <KeyRound size={13} /> {newPin}
                      </span>
                      <CopyChip value={newPin} label="PIN" />
                    </dd>
                  </div>
                </dl>
              ) : null}
              <div className="onboard-actions">
                <button
                  className="ev-btn ev-btn-outline ev-btn-sm"
                  type="button"
                  disabled={resetPin.isPending}
                  onClick={() => resetPin.mutate({ id: selected.id })}
                >
                  <KeyRound size={15} /> Reset sign-in PIN
                </button>
              </div>
            </Section>

            <Section
              title="Network"
              hint="An agent only pays this dealer the 0.5% slice once it is in the network. Agents the dealer recruited itself are already here."
            >
              {agentsQ.isLoading ? <LoadingCard /> : null}

              {networkAgents.length > 0 && (
                <ul className="network-list">
                  {networkAgents.map((agent) => (
                    <li key={agent.id} className="network-item">
                      <span className="ev-body-mono">{agent.code}</span>
                      <span className="ev-body">{agent.business_name}</span>
                      <span className="ev-body-micro muted">
                        {tzs(agent.available_minor)} float · {agent.status}
                      </span>
                      <button
                        className="ev-btn ev-btn-ghost ev-btn-sm"
                        type="button"
                        disabled={assign.isPending}
                        onClick={() => assign.mutate({ agentId: agent.id, dealer_id: null })}
                      >
                        <X size={15} /> Detach
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {networkAgents.length === 0 && !agentsQ.isLoading && (
                <p className="ev-body-micro muted">No agents in this network yet.</p>
              )}

              <div className="onboard-pin-row">
                <select
                  className="ev-input"
                  aria-label="Agent to attach"
                  value={attachPick}
                  onChange={(e) => setAttachPick(e.target.value)}
                >
                  <option value="">
                    {unattached.length ? "Attach an existing agent…" : "No unassigned agents left"}
                  </option>
                  {unattached.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.code} · {agent.business_name} · {agent.phone}
                    </option>
                  ))}
                </select>
                <button
                  className="ev-btn ev-btn-outline ev-btn-sm"
                  type="button"
                  disabled={!attachPick || assign.isPending}
                  onClick={() => assign.mutate({ agentId: attachPick, dealer_id: selected.id })}
                >
                  <Plus size={15} /> Attach
                </button>
              </div>
            </Section>

            <Section title="Credit float" hint="Use this when the bank statement already shows the transfer.">
              <div className="onboard-grid-2">
                <div className="admin-field">
                  <label htmlFor="sd-fund-amount">Amount (TZS)</label>
                  <input
                    id="sd-fund-amount"
                    className="ev-input"
                    inputMode="numeric"
                    value={edit.float_amount}
                    onChange={(e) => setEdit({ ...edit, float_amount: e.target.value.replace(/[^\d.]/g, "") })}
                    placeholder="500000"
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="sd-fund-ref">Reference</label>
                  <input
                    id="sd-fund-ref"
                    className="ev-input"
                    value={edit.float_reference}
                    onChange={(e) => setEdit({ ...edit, float_reference: e.target.value })}
                    placeholder="CRDB-0411-88213"
                  />
                </div>
              </div>
              <button
                className="ev-btn ev-btn-primary"
                type="button"
                disabled={fund.isPending || toMinor(edit.float_amount) <= 0}
                onClick={() =>
                  fund.mutate({
                    id: selected.id,
                    amount_minor: toMinor(edit.float_amount),
                    reference: edit.float_reference.trim(),
                  })
                }
              >
                <Wallet size={16} /> Credit {tzs(toMinor(edit.float_amount))}
              </button>
            </Section>
          </div>
        </div>
      )}
    </>
  );
}
